import { useState, useRef, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import { useLocation } from "react-router-dom";
import { X, ArrowUp, Check, Copy, Search, Target, Sunrise, Volume2, VolumeX, Bell, BellRing, RotateCcw, Mic, Square, Headphones, Eye, Calculator, Mail, Factory, Users, Wallet, Store, Paperclip } from "lucide-react";
import { api } from "../context/AuthContext";
import { AREA_URLS } from "../config/areaUrls";
import { VikiAvatar, THEME_FOR_APP } from "./VikiAvatar";
import { playVikiReply, playVikiNotify, vikiSoundsEnabled, setVikiSoundsEnabled, speakViki, stopVikiSpeech } from "../lib/vikiSounds";
import { streamVikiChat } from "../lib/vikiStream";

/* ---------- app di provenienza (dal sottodominio) e personalizzazione:
   VIKI "indossa" un piccolo distintivo a tema in base all'app ---------- */
const APP_KEY = (() => {
  const m = window.location.hostname.toLowerCase().match(/^([a-z0-9-]+)\.areapaq\.it$/);
  const sub = m ? m[1] : "";
  return ["posta", "tesoreria", "contabilita", "produzione", "risorice", "paghe", "sohelp", "paqway"].includes(sub) ? sub : "areapaq";
})();
const APP_BADGE = {
  contabilita: { Icon: Calculator, flavor: "la tua contabile AI" },
  tesoreria: { Icon: Calculator, flavor: "la tua tesoriera AI" },
  posta: { Icon: Mail, flavor: "la tua postina AI" },
  produzione: { Icon: Factory, flavor: "assistente di produzione" },
  risorice: { Icon: Users, flavor: "assistente risorse" },
  paghe: { Icon: Wallet, flavor: "assistente paghe" },
  paqway: { Icon: Store, flavor: "assistente commerciale" },
}[APP_KEY] || null;
// Avatar vestito a tema: se esiste, sostituisce il distintivo-icona.
const APP_THEME = THEME_FOR_APP[APP_KEY] || null;

/* ---------- identità visiva di VIKI: stessa palette ciano usata nel
   pannello dedicato /viki, distinta dal resto dell'app di proposito ---------- */
const C = {
  bg: "#0A0E13",
  surface: "#111820",
  surfaceAlt: "#161F29",
  border: "#22303C",
  text: "#FFFFFF",
  muted: "rgba(255,255,255,0.88)",
  mutedDim: "rgba(255,255,255,0.68)",
  cyan: "#45E0D0",
  cyanDim: "rgba(69,224,208,0.14)",
  violet: "#9C8FF0",
  violetDim: "rgba(156,143,240,0.14)",
};

/* ---------- pagine dove VIKI sa eseguire davvero un'azione proposta,
   invece di limitarsi a proporla a parole. Tenerle qui, in un unico
   punto, rende facile aggiungerne altre in futuro senza toccare il
   resto del componente. ---------- */
const CROSSAPP_LABELS = { tesoreria: "Tesoreria", posta: "Posta AI", contabilita: "Contabilità", produzione: "Produzione", risorice: "Risorice", paghe: "Paghe", sohelp: "So.Help", paqway: "PAQ.WAY" };

// Etichetta mostrata nel widget mentre VIKI esegue il secondo passo di
// uno strumento a due fasi (chiamata /chat, poi /chat/continue). Prima
// c'erano solo "searching" e "fetching" gestiti esplicitamente — un
// bug reale, corretto qui: gli altri tre stati (base di conoscenza,
// promemoria, calcolo) cadevano nel ramo "else" che si aspetta
// data.reply, un campo che quegli stati non hanno, lasciando la
// conversazione silenziosamente bloccata a metà.
const TWO_PHASE_STATUS_LABELS = {
  searching: (data) => `Sto cercando: ${data.query}`,
  market_scan: (data) => `Sto analizzando la zona: ${data.query}`,
  fetching: (data) => `Sto consultando ${CROSSAPP_LABELS[data.area] || data.area}…`,
  consulting_kb: (data) => `Sto consultando la base di conoscenza: ${data.query}`,
  checking_reminders: () => "Controllo i tuoi promemoria…",
  recalling_memory: () => "Rileggo la mia memoria su di te…",
  calculating: (data) => `Sto calcolando: ${data.expression}`,
  checking_usage_log: () => "Consulto il registro d'uso…",
  checking_db: () => "Controllo lo stato dei database…",
  analyzing_ecosystem: () => "Sto analizzando i dati dell'ecosistema…",
  scanning: () => "Scansione completa dell'ecosistema: KPI, cassa, avvisi…",
  checking_portal: (d) => `Sto consultando il portale (${d.section || "dati"})…`,
};

// Suggerimenti mostrati a conversazione vuota: SOLO due domande, come
// richiesto — panoramica dell'app corrente e capacità di VIKI.
const SUGGESTION_APP_LABEL = CROSSAPP_LABELS[APP_KEY] || "AREAPAQ";
const QUICK_SUGGESTIONS = [
  `Panoramica dei dati di ${SUGGESTION_APP_LABEL}`,
  "Cosa sai fare?",
];

function suggestionsFor() {
  return QUICK_SUGGESTIONS;
}

// Azioni eseguibili altrove (non qui) tramite il backend centralizzato:
// se l'app corrente non può eseguirle in locale, il pulsante diventa
// "Esegui in [app]" invece di "Copia contenuto".
const CROSSAPP_EXECUTABLE = { tesoreria_add_movement: "tesoreria", posta_draft_email: "posta" };

const DIRECTION_LABEL = { incasso: "Incasso", spesa: "Spesa" };

// Anteprime per azioni che questa app non sa eseguire in locale, ma può
// comunque mostrare ed eseguire cross-app tramite AREAPAQ.
const ACTION_PREVIEWS = {
  posta_draft_email: (action) => (
    <div style={{ color: C.text, fontSize: 12 }}>
      <div><b>A:</b> {action.to || "—"}</div>
      {action.cc && <div><b>CC:</b> {action.cc}</div>}
      <div style={{ marginTop: 4 }}><b>{action.subject || "(nessun oggetto)"}</b></div>
      <div style={{ color: C.muted, marginTop: 3, whiteSpace: "pre-wrap" }}>{action.body}</div>
    </div>
  ),
  tesoreria_add_movement: (action) => (
    <div style={{ color: C.text, fontSize: 12 }}>
      <div><b>{DIRECTION_LABEL[action.direction] || action.direction}</b> — {action.description}</div>
      <div style={{ color: C.muted, marginTop: 2 }}>
        {action.amount != null ? `€ ${Number(action.amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"} · scadenza {action.due_date || "—"}
      </div>
      {action.category && <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 2 }}>{action.category}</div>}
      {action.note && <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 2 }}>{action.note}</div>}
    </div>
  ),
};

const TARGET_LABELS = {
  produzione: "Produzione", contabilita: "Contabilità", tesoreria: "Tesoreria",
  risorice: "Risorice", posta: "Posta AI", paghe: "Paghe", sohelp: "So.Help", paqway: "PAQ.WAY",
  portale: "Portale AREAPAQ", tutti: "Tutti i moduli",
};

const DOC_FORMAT_LABEL = { pdf: "PDF", docx: "Word", xlsx: "Excel" };

// Anteprima: apre il documento (inline nel browser) o l'email impaginata
// in una scheda già aperta in modo sincrono (w), per non farsi bloccare
// dal blocco popup dopo una chiamata async.
const apiOrigin = () => (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
const previewDocInWindow = (w, downloadUrl) => {
  w.location.href = `${apiOrigin()}${downloadUrl}&inline=1`;
};
const previewEmailInWindow = async (w, bodyText) => {
  const res = await api.post("/viki/email/preview", { body: bodyText || "" },
    { responseType: "text", transformResponse: [(d) => d] });
  const blob = new Blob([res.data], { type: "text/html" });
  const blobUrl = URL.createObjectURL(blob);
  w.location.href = blobUrl;
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

const ACTION_HANDLERS = {
  viki_memory_save: {
    matchesPage: () => true,
    label: "Salva nella memoria",
    doneLabel: "Salvato in memoria",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          Ricordare questo fatto{" "}
          <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· {action.category || "contesto"}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{action.fact}</div>
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 3 }}>
          Entra in memoria solo se confermi — puoi cancellarlo quando vuoi dalla pagina V.I.K.I.
        </div>
      </>
    ),
    run: async (action) => {
      await api.post("/viki/memory", {
        fact: action.fact || "",
        category: action.category || "contesto",
        source_session_id: action.source_session_id || "",
      });
    },
  },
  viki_memory_forget: {
    matchesPage: () => true,
    label: "Dimentica",
    doneLabel: "Cancellato dalla memoria",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {(action.facts || []).length === 1 ? "Dimenticare questo fatto" : `Dimenticare questi ${(action.facts || []).length} fatti`}
        </div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {(action.facts || []).map((f) => `• ${f}`).join("\n")}
        </div>
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 3 }}>
          La cancellazione avviene solo se confermi — e non è reversibile.
        </div>
      </>
    ),
    run: async (action) => {
      for (const id of action.fact_ids || []) {
        await api.delete(`/viki/memory/${id}`);
      }
    },
  },
  viki_export_report: {
    matchesPage: () => true,
    label: "Genera e scarica",
    doneLabel: "File scaricato",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {action.title || "Report VIKI"}{" "}
          <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· {action.format === "markdown" ? "Markdown" : "PDF"}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, maxHeight: 72, overflow: "hidden", whiteSpace: "pre-wrap" }}>
          {(action.content || "").slice(0, 220)}
        </div>
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 3 }}>Il link di download vale 24 ore</div>
      </>
    ),
    run: async (action) => {
      const { data } = await api.post("/viki/export/generate", {
        title: action.title || "Report VIKI",
        format: action.format || "pdf",
        content: action.content || "",
      });
      const res = await api.get(`/viki/documents/${data.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    canPreview: (action) => (action.format || "pdf").toLowerCase() === "pdf",
    previewRun: async (action, w) => {
      const { data } = await api.post("/viki/export/generate", {
        title: action.title || "Report VIKI",
        format: action.format || "pdf",
        content: action.content || "",
      });
      previewDocInWindow(w, data.download_url);
    },
  },
  viki_document: {
    matchesPage: () => true,
    label: "Genera e scarica",
    doneLabel: "Documento scaricato",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {action.title || "Documento"}{" "}
          <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· {DOC_FORMAT_LABEL[action.format] || action.format}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11 }}>
          {(action.sections || []).length} sezioni
          {(action.sections || []).some((s) => s?.table) ? " · con tabelle" : ""}
          {(action.sections || []).some((s) => s?.chart) ? " · con grafici" : ""}
        </div>
      </>
    ),
    run: async (action) => {
      const { data } = await api.post("/viki/documents/generate", action);
      const res = await api.get(`/viki/documents/${data.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    canPreview: (action) => (action.format || "").toLowerCase() === "pdf",
    previewRun: async (action, w) => {
      const { data } = await api.post("/viki/documents/generate", action);
      previewDocInWindow(w, data.download_url);
    },
  },
  viki_document_email: {
    matchesPage: () => true,
    label: "Conferma e invia",
    doneLabel: "Eseguito",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {action.subject || action.title || "Documento via email"}{" "}
          <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· {DOC_FORMAT_LABEL[action.format] || action.format}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 2 }}>
          A: {(Array.isArray(action.to) ? action.to : [action.to]).filter(Boolean).join(", ") || "—"}
        </div>
        <div style={{ color: C.mutedDim, fontSize: 10.5 }}>
          {action.via === "personale"
            ? "Bozza sulla tua casella (Posta AI) con link al documento"
            : "Invio diretto dal portale con documento allegato"}
        </div>
      </>
    ),
    run: async (action) => {
      await api.post("/viki/documents/email", action);
    },
    previewRun: async (action, w) => previewEmailInWindow(w, action.body || ""),
  },
  market_report: {
    matchesPage: () => true,
    label: "Genera report PDF",
    doneLabel: "Report PDF scaricato",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          Report Indagine di Mercato <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· PDF</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11 }}>
          "{action.category}" entro {action.radius || 500} m da {action.address}
        </div>
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 2 }}>
          Mappa reale della zona, analisi di VIKI, grafico e tabella completa (richiede qualche decina di secondi)
        </div>
      </>
    ),
    run: async (action) => {
      const { data } = await api.post("/market/report", {
        address: action.address || "",
        category: action.category || "",
        radius: Number(action.radius) || 500,
      });
      const res = await api.get(`/viki/documents/${data.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    previewRun: async (action, w) => {
      const { data } = await api.post("/market/report", {
        address: action.address || "",
        category: action.category || "",
        radius: Number(action.radius) || 500,
      });
      previewDocInWindow(w, data.download_url);
    },
  },
  viki_send_email: {
    matchesPage: () => true,
    label: "Conferma e invia",
    doneLabel: "Email inviata",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {action.subject || "Email"} <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· Email</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 2 }}>
          A: {(Array.isArray(action.to) ? action.to : [action.to]).filter(Boolean).join(", ") || "—"}
        </div>
        {action.body && (
          <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, maxHeight: 72, overflow: "hidden", whiteSpace: "pre-wrap" }}>
            {action.body}
          </div>
        )}
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 3 }}>
          Layout AREAPAQ: logo, data, testo e firma "V.I.K.I - Assistente AI"
        </div>
      </>
    ),
    run: async (action) => {
      await api.post("/viki/email/send", action);
    },
    previewRun: async (action, w) => previewEmailInWindow(w, action.body || ""),
  },
  finance_report: {
    matchesPage: () => true,
    label: "Genera quadro PDF",
    doneLabel: "Quadro PDF scaricato",
    preview: () => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          Quadro Economico-Finanziario <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· PDF</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11 }}>
          Dati letti in tempo reale da Contabilità e Tesoreria (saldi, movimenti, KPI)
        </div>
        <div style={{ color: C.mutedDim, fontSize: 10.5, marginTop: 2 }}>
          Analisi di VIKI con tabelle e grafici, firmato "V.I.K.I - Assistente AI" (richiede qualche decina di secondi)
        </div>
      </>
    ),
    run: async () => {
      const { data } = await api.post("/viki/finance-report", {});
      const res = await api.get(`/viki/documents/${data.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    previewRun: async (_action, w) => {
      const { data } = await api.post("/viki/finance-report", {});
      previewDocInWindow(w, data.download_url);
    },
  },
  marketing_email_draft: {
    matchesPage: (path) => path === "/marketing/email/template",
    label: "Aggiungi come bozza email",
    doneLabel: "Aggiunta come bozza",
    preview: (action) => (
      <>
        {action.subject && <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{action.subject}</div>}
        {action.html && (
          <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, maxHeight: 90, overflow: "hidden" }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(action.html) }} />
        )}
      </>
    ),
    run: async (action) => {
      await api.post("/admin/marketing/newsletter/drafts", {
        subject: action.subject || "",
        html: action.html || "",
        recipients: [],
      });
    },
  },
  site_note: {
    matchesPage: (path) => path === "/web" || path === "/monitor/siti",
    label: "Salva promemoria sito",
    doneLabel: "Promemoria salvato",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{action.domain}</div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5 }}>{action.note}</div>
      </>
    ),
    run: async (action) => {
      await api.put(`/admin/web/notes/${action.domain}`, { note: action.note || "" });
    },
  },
  tracked_link: {
    matchesPage: (path) => path === "/marketing/strumenti",
    label: "Crea link tracciato",
    doneLabel: "Link creato",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
          {action.name || "Link tracciato"}{" "}
          <span style={{ color: C.cyan, fontSize: 10, fontWeight: 600 }}>· {action.kind === "qr" ? "QR code" : "UTM"}</span>
        </div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, wordBreak: "break-all" }}>{action.target_url}</div>
      </>
    ),
    run: async (action) => {
      await api.post("/admin/tools/links", {
        name: action.name || "",
        target_url: action.target_url,
        kind: action.kind === "qr" ? "qr" : "utm",
      });
    },
  },
  marketing_contact_add: {
    matchesPage: (path) => path === "/marketing/email/liste",
    label: "Aggiungi contatto alla lista",
    doneLabel: "Contatto aggiunto",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Lista: {action.list_name}</div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5 }}>
          {[action.email, action.phone].filter(Boolean).join(" · ") || "nessun dato contatto"}
        </div>
      </>
    ),
    run: async (action) => {
      const { data: lists } = await api.get("/admin/marketing/lists");
      const target = (lists || []).find(
        (l) => (l.name || "").trim().toLowerCase() === (action.list_name || "").trim().toLowerCase()
      );
      if (!target) throw new Error("Lista non trovata");
      await api.post(`/admin/marketing/lists/${target.id}/contacts`, {
        email: action.email || null,
        phone: action.phone || null,
      });
    },
  },
  marketing_list_create: {
    matchesPage: (path) => path === "/marketing/email/liste",
    label: "Crea la lista",
    doneLabel: "Lista creata",
    preview: (action) => (
      <>
        <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Nuova lista: {action.name}</div>
        <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5 }}>
          {(action.emails || []).length + (action.phones || []).length > 0
            ? [...(action.emails || []), ...(action.phones || [])].join(" · ")
            : "vuota, pronta per i contatti"}
        </div>
      </>
    ),
    run: async (action) => {
      await api.post("/admin/marketing/lists", {
        name: (action.name || "").trim(),
        emails: action.emails || [],
        phones: action.phones || [],
      });
    },
  },
  areapaq_backup: {
    matchesPage: () => true, // azione globale, non legata a una singola pagina
    label: "Genera backup",
    doneLabel: "Backup avviato",
    preview: (action) => (
      <div style={{ color: C.text, fontSize: 12 }}>
        Backup di: <b>{TARGET_LABELS[action.target] || action.target}</b>
        {action.send ? <span style={{ color: C.muted }}> · verrà inviato via email ai Master</span> : null}
      </div>
    ),
    run: async (action) => {
      if (action.send) {
        const { data } = await api.post("/admin/maintenance/backup-email", { target: action.target });
        return data.message;
      }
      const res = await api.post("/admin/maintenance/backup", { target: action.target }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `BKP_${action.target}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
  },
  open_app: {
    matchesPage: () => true,
    label: "Apri l'app",
    doneLabel: "Ti sto portando lì…",
    preview: (action) => (
      <div style={{ color: C.text, fontSize: 12 }} data-testid="viki-open-app-preview">
        Apro <b>{TARGET_LABELS[action.area] || action.area}</b> e mi faccio trovare lì già aperta.
      </div>
    ),
    run: async (action) => {
      const base = AREA_URLS[action.area];
      if (!base) throw new Error("App non configurata");
      const url = base.includes("?") ? `${base}&viki=open` : `${base}?viki=open`;
      setTimeout(() => { window.location.href = url; }, 500);
    },
  },
  areapaq_db_optimize: {
    matchesPage: (path) => path === "/manutenzione",
    label: "Ottimizza database",
    doneLabel: "Ottimizzazione completata",
    preview: (action) => (
      <div style={{ color: C.text, fontSize: 12 }}>
        Ottimizza: <b>{TARGET_LABELS[action.target] || action.target}</b>
      </div>
    ),
    run: async (action) => {
      await api.post("/admin/maintenance/optimize", { target: action.target });
    },
  },
  areapaq_smtp_update: {
    matchesPage: (path) => path.startsWith("/impostazioni"),
    label: "Applica modifica SMTP",
    doneLabel: "Configurazione aggiornata",
    preview: (action) => {
      const { type, kind, ...fields } = action;
      return (
        <div style={{ color: C.text, fontSize: 12 }}>
          SMTP <b>{kind}</b> — {Object.keys(fields).filter((k) => k !== "password").join(", ") || "nessuna modifica specificata"}
          {fields.password && <span style={{ color: C.mutedDim }}> · password inclusa (non mostrata)</span>}
        </div>
      );
    },
    run: async (action) => {
      const { type, kind, ...changes } = action;
      // l'endpoint reale sostituisce l'intera configurazione: leggo prima quella
      // attuale e applico sopra solo i campi che l'utente ha specificato, così
      // non si perdono per errore gli altri valori già salvati.
      const { data: current } = await api.get("/admin/settings/smtp");
      const existing = current?.[kind] || {};
      const merged = {
        host: existing.host || "", port: existing.port ?? 587, username: existing.username || "",
        use_tls: existing.use_tls ?? true, from_email: existing.from_email || "",
        recipients: existing.recipients || [], enabled: existing.enabled ?? true,
        ...changes,
      };
      await api.put(`/admin/settings/smtp/${kind}`, merged);
    },
  },
  viki_reminder: {
    matchesPage: () => true, // sempre eseguibile: vive in AREAPAQ, non legata a nessuna app specifica
    label: "Imposta promemoria",
    doneLabel: "Promemoria impostato",
    preview: (action) => (
      <div style={{ color: C.text, fontSize: 12 }}>
        <div>{action.text}</div>
        <div style={{ color: C.muted, marginTop: 2 }}>Scade: {action.due_at || "—"}</div>
      </div>
    ),
    run: async (action) => {
      await api.post("/viki/reminders", { text: action.text || "", due_at: action.due_at || "" });
    },
  },
  user_create: {
    matchesPage: () => true,
    label: "Crea utente",
    doneLabel: "Utente creato",
    preview: (action) => (
      <div style={{ color: C.text, fontSize: 12 }}>
        <div><b>{action.name || action.email}</b> · ruolo <b>{action.role || "user"}</b></div>
        <div style={{ color: C.muted, marginTop: 2 }}>{action.email}</div>
        {action.password && <div style={{ color: C.muted, marginTop: 2 }}>Password iniziale: <b>{action.password}</b></div>}
        <div style={{ color: C.mutedDim, marginTop: 2, fontSize: 10.5 }}>
          Card: {(action.tiles || []).length ? action.tiles.join(", ") : "tutte"}
        </div>
      </div>
    ),
    run: async (action) => {
      await api.post("/admin/users", {
        email: action.email, password: action.password, name: action.name || "",
        role: action.role || "user", tiles: action.tiles || null,
      });
    },
    confirmField: (action) => ({
      prompt: "Per confermare, ridigita l'email del nuovo utente:",
      placeholder: action.email,
      expected: (action.email || "").trim().toLowerCase(),
      transform: (v) => v.trim().toLowerCase(),
    }),
  },
  user_update: {
    matchesPage: () => true,
    label: "Applica modifica utente",
    doneLabel: "Utente aggiornato",
    preview: (action) => {
      const { type, email, password, ...fields } = action;
      return (
        <div style={{ color: C.text, fontSize: 12 }}>
          <div>Utente: <b>{email}</b></div>
          <div style={{ color: C.muted, marginTop: 2 }}>
            Modifiche: {Object.keys(fields).join(", ") || "—"}{password ? (Object.keys(fields).length ? ", " : "") + "nuova password" : ""}
          </div>
        </div>
      );
    },
    run: async (action) => {
      const { data: users } = await api.get("/admin/users");
      const target = (users || []).find((u) => (u.email || "").toLowerCase() === (action.email || "").trim().toLowerCase());
      if (!target) throw new Error("Utente non trovato");
      const { type, email, ...changes } = action;
      await api.put(`/admin/users/${target.id}`, changes);
    },
    confirmField: (action) => action.role ? ({
      prompt: `Cambio di RUOLO per ${action.email} → ${action.role}. Per confermare scrivi: CAMBIA RUOLO`,
      placeholder: "CAMBIA RUOLO",
      expected: "CAMBIA RUOLO",
      transform: (v) => v.trim().toUpperCase(),
    }) : null,
  },
  webapp_doctor: {
    matchesPage: () => true,
    label: "Avvia visita Doctor",
    doneLabel: "Visita completata",
    preview: () => (
      <div style={{ color: C.text, fontSize: 12 }}>
        Ricontrolla tutti i collegamenti delle app (sito, API, SSO, sync, dati, VIKI) — circa un minuto.
      </div>
    ),
    run: async (action) => {
      await api.post("/admin/webapp/doctor", {}, { timeout: 180000 });
    },
  },
  ecosystem_cure: {
    matchesPage: () => true,
    label: "Avvia la cura dell'ecosistema",
    doneLabel: "Cura completata",
    preview: () => (
      <div style={{ color: C.text, fontSize: 12 }} data-testid="viki-ecosystem-cure-preview">
        Diagnosi fresca di tutte le app, riparazioni automatiche (utenti SSO mancanti, dati KPI fermi,
        cache) e verifica finale con confronto prima/dopo — 1-2 minuti. Nota: gli endpoint assenti
        dentro le singole app (stati "missing"/"pending") non sono riparabili dalla cura.
      </div>
    ),
    run: async (action) => {
      const { data } = await api.post("/admin/ecosystem/cure", {}, { timeout: 300000 });
      const rip = (data.riparazioni || []).map((r) => `• ${r}`).join("\n");
      const nc = data.problemi_non_curabili_dalla_cura || [];
      let note = `Esito: ${data.esito}.\n${rip}`;
      if (nc.length) {
        note += `\n\nRestano ${nc.length} punti che la cura NON può risolvere (endpoint da implementare dentro le app):\n` + nc.map((r) => `• ${r}`).join("\n");
      }
      return note;
    },
  },
  playbook_candidate: {
    matchesPage: () => true,
    label: "Salva come rimedio candidato",
    doneLabel: "Candidato salvato nel playbook",
    preview: (a) => (
      <div style={{ color: C.text, fontSize: 12 }} data-testid="viki-playbook-candidate-preview">
        "{a.titolo}" verrà salvato nel playbook come CANDIDATO: resterà fuori dalla corsia veloce
        finché un Master non lo promuove a collaudato con un'azione separata.
      </div>
    ),
    run: async (action) => {
      await api.post("/viki/playbook/candidate", action);
    },
  },
  playbook_promote: {
    matchesPage: () => true,
    label: "Promuovi a collaudato",
    doneLabel: "Promosso a collaudato",
    preview: (a) => (
      <div style={{ color: C.text, fontSize: 12 }} data-testid="viki-playbook-promote-preview">
        Il rimedio "{a.playbook_id}" entrerà nella corsia veloce a un clic per i prossimi errori uguali.
        Solo un Master può confermare.
      </div>
    ),
    run: async (action) => {
      await api.post(`/viki/playbook/${action.playbook_id}/promote`);
    },
  },
};

function FormattedText({ text }) {
  // Formattazione leggera per la lettura: **grassetto**, elenchi puntati
  // ("-"/"*"), elenchi numerati ("1." / "1)") e titoli di sezione ("## ").
  const lines = (text || "").split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const heading = line.match(/^\s*#{2,4}\s+(.*)/);
        const bulletMatch = !heading && line.match(/^(\s*)[-*]\s+(.*)/);
        const numMatch = !heading && !bulletMatch && line.match(/^(\s*)(\d{1,2})[.)]\s+(.*)/);
        const content = heading ? heading[1] : bulletMatch ? bulletMatch[2] : numMatch ? numMatch[3] : line;
        const parts = content.split(/(\*\*[^*]+\*\*)/g).filter((p) => p !== "");
        const rendered = parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**") && part.length > 4
            ? <b key={j}>{part.slice(2, -2)}</b>
            : <span key={j}>{part}</span>
        );
        if (heading) {
          return (
            <span key={i} style={{ fontWeight: 800, fontSize: "1.05em", color: "#DFF9F5" }}>
              {rendered}
              {i < lines.length - 1 && "\n"}
            </span>
          );
        }
        return (
          <span key={i}>
            {bulletMatch && "• "}
            {numMatch && `${numMatch[2]}. `}
            {rendered}
            {i < lines.length - 1 && "\n"}
          </span>
        );
      })}
    </>
  );
}

function MultiActionCard({ actions }) {
  const [status, setStatus] = useState("idle"); // idle | working | done | error
  const [errMsg, setErrMsg] = useState("");
  const [doneCount, setDoneCount] = useState(0);

  const runAll = async () => {
    if (status === "working" || status === "done") return;
    setStatus("working");
    setErrMsg("");
    let ok = 0;
    for (const a of actions) {
      const h = ACTION_HANDLERS[a.type];
      if (!h) {
        setErrMsg(`Un'azione proposta non è eseguibile da qui (${a.type}): nessuna delle ${actions.length} è stata eseguita oltre le ${ok} già fatte.`);
        setStatus("error");
        setDoneCount(ok);
        return;
      }
      try {
        await h.run(a);
        ok += 1;
        setDoneCount(ok);
      } catch {
        setErrMsg(`L'azione ${ok + 1} di ${actions.length} non è riuscita: mi sono fermata qui (eseguite: ${ok}).`);
        setStatus("error");
        return;
      }
    }
    setStatus("done");
  };

  return (
    <div className="mt-2 p-2.5 rounded-md" data-testid="viki-multi-action-card"
      style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
      <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
        {actions.length} azioni proposte insieme
      </div>
      <div className="space-y-2">
        {actions.map((a, i) => {
          const h = ACTION_HANDLERS[a.type];
          const preview = h?.preview || ACTION_PREVIEWS[a.type];
          return (
            <div key={i} className="rounded-md px-2.5 py-2" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
              <div style={{ color: C.mutedDim, fontSize: 9.5, fontWeight: 700, marginBottom: 2 }}>{i + 1} di {actions.length}</div>
              {preview ? preview(a) : <div style={{ color: C.muted, fontSize: 11 }}>{a.type}</div>}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-2.5">
        <button onClick={runAll} disabled={status === "working" || status === "done"} data-testid="viki-multi-action-confirm-all"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
          style={{ background: status === "done" ? "rgba(69,224,208,0.25)" : C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
          <Check size={12} />
          {status === "working" ? `Eseguo… (${doneCount}/${actions.length})` : status === "done" ? "Tutte eseguite" : `Conferma tutto (${actions.length})`}
        </button>
      </div>
      {errMsg && <div style={{ color: "#E0616F", fontSize: 10.5, marginTop: 6 }}>{errMsg}</div>}
    </div>
  );
}

function ProposedActionCard({ action, currentPath, onDone }) {
  const [status, setStatus] = useState("idle"); // idle | working | done | error | copied
  const [pvStatus, setPvStatus] = useState("idle"); // idle | working | error
  const [confirmText, setConfirmText] = useState("");
  const [resultNote, setResultNote] = useState("");
  const handler = ACTION_HANDLERS[action.type];
  const canRunHere = handler && handler.matchesPage(currentPath);
  const crossAppArea = !canRunHere ? CROSSAPP_EXECUTABLE[action.type] : null;
  const preview = handler?.preview || ACTION_PREVIEWS[action.type];
  const canPreviewHere = canRunHere && handler.previewRun && (!handler.canPreview || handler.canPreview(action));
  const confirmReq = canRunHere && handler.confirmField ? handler.confirmField(action) : null;
  const confirmOk = !confirmReq || confirmReq.transform(confirmText) === confirmReq.expected;

  const handlePreview = async () => {
    if (pvStatus === "working") return;
    setPvStatus("working");
    const w = window.open("", "_blank"); // aperta subito: dopo l'await il popup verrebbe bloccato
    try {
      await handler.previewRun(action, w);
      setPvStatus("idle");
    } catch {
      try { w && w.close(); } catch { /* già chiusa */ }
      setPvStatus("error");
    }
  };

  const handleAdd = async () => {
    if (!canRunHere || !confirmOk) return;
    setStatus("working");
    try {
      const note = await handler.run(action);
      if (typeof note === "string" && note) setResultNote(note);
      setStatus("done");
      // Governance playbook: conta l'applicazione del rimedio collaudato
      if (action.playbook_id && action.type !== "playbook_promote") {
        api.post(`/viki/playbook/${action.playbook_id}/applied`).catch(() => {});
      }
      onDone?.();
    } catch {
      setStatus("error");
    }
  };

  const handleRefuse = async () => {
    setStatus("refused");
    if (action.playbook_id) {
      api.post(`/viki/playbook/${action.playbook_id}/refused`).catch(() => {});
    }
  };

  const handleCrossAppExec = async () => {
    setStatus("working");
    try {
      // AREAPAQ è già il backend centralizzato: qui "api" è locale.
      await api.post("/viki/crossapp-execute", { area: crossAppArea, action_type: action.type, payload: action });
      setStatus("done");
      onDone?.();
    } catch {
      setStatus("error");
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(action, null, 2));
      setStatus("copied");
    } catch {
      /* clipboard non disponibile: nessuna azione, il testo resta comunque visibile sopra */
    }
  };

  return (
    <div className="mt-2 p-2.5 rounded-md" style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}>
      {preview ? preview(action) : null}
      {resultNote && status === "done" && (
        <div data-testid="viki-action-result-note" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8,
             background: "rgba(69,224,208,0.07)", border: "1px solid rgba(69,224,208,0.25)",
             color: C.text, fontSize: 11.5, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {resultNote}
        </div>
      )}
      {confirmReq && status !== "done" && (
        <div className="mt-2" data-testid="viki-action-confirm-field">
          <div style={{ color: "#E9B44C", fontSize: 10.5, fontWeight: 600, marginBottom: 4 }}>{confirmReq.prompt}</div>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={confirmReq.placeholder}
            data-testid="viki-action-confirm-input"
            className="w-full px-2 py-1.5 rounded-md text-[11px] outline-none"
            style={{ background: "rgba(255,255,255,0.05)", color: C.text, border: `1px solid ${confirmOk && confirmText ? C.cyan + "88" : C.border}` }} />
        </div>
      )}
      <div className="flex items-center gap-2 mt-2.5">
        {canRunHere ? (
          <button onClick={handleAdd} disabled={status === "working" || status === "done" || status === "refused" || !confirmOk}
            data-testid="viki-action-confirm-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ background: status === "done" ? "rgba(69,224,208,0.25)" : C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55`,
                     opacity: !confirmOk && status === "idle" ? 0.45 : 1 }}>
            <Check size={12} />
            {status === "working" ? "Eseguo…" : status === "done" ? handler.doneLabel : handler.label}
          </button>
        ) : crossAppArea ? (
          <button onClick={handleCrossAppExec} disabled={status === "working" || status === "done"}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ background: status === "done" ? "rgba(69,224,208,0.25)" : C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
            <Check size={12} />
            {status === "working" ? "Eseguo…" : status === "done" ? "Eseguita" : `Esegui in ${CROSSAPP_LABELS[crossAppArea] || crossAppArea}`}
          </button>
        ) : (
          <button onClick={handleCopy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ background: C.surfaceAlt, color: C.muted, border: `1px solid ${C.border}` }}>
            <Copy size={12} /> {status === "copied" ? "Copiato" : "Copia contenuto"}
          </button>
        )}
        {canPreviewHere && (
          <button data-testid="viki-action-preview-btn" onClick={handlePreview} disabled={pvStatus === "working"}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ background: C.surfaceAlt, color: C.muted, border: `1px solid ${C.border}` }}>
            <Eye size={12} /> {pvStatus === "working" ? "Preparo…" : "Anteprima"}
          </button>
        )}
        {action.playbook_id && canRunHere && status !== "done" && (
          <button data-testid="viki-action-refuse-btn" onClick={handleRefuse} disabled={status === "working" || status === "refused"}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
            style={{ background: C.surfaceAlt, color: status === "refused" ? "#E0616F" : C.muted, border: `1px solid ${C.border}` }}>
            <X size={12} /> {status === "refused" ? "Rifiutata" : "Rifiuta"}
          </button>
        )}
      </div>
      {!canRunHere && !crossAppArea && (
        <div style={{ color: C.mutedDim, fontSize: 10, marginTop: 6 }}>
          Per eseguirlo automaticamente, chiedimelo dalla pagina giusta.
        </div>
      )}
      {status === "error" && <div style={{ color: "#E0616F", fontSize: 10.5, marginTop: 6 }}>Non sono riuscita a eseguirla. Riprova.</div>}
      {pvStatus === "error" && <div style={{ color: "#E0616F", fontSize: 10.5, marginTop: 6 }}>Anteprima non riuscita. Riprova.</div>}
    </div>
  );
}

/* ---------- saluto consapevole di ora e pagina: il primo contatto è
   contestuale, senza inventare nulla — orologio reale + rotta reale. ---------- */
const PAGE_HELLO = [
  { match: (p) => p === "/menu", line: "Hai la vista d'insieme davanti: chiedimi il punto della situazione quando vuoi." },
  { match: (p) => p === "/manutenzione", line: "Siamo in Manutenzione: posso controllare i database o preparare un backup." },
  { match: (p) => p.startsWith("/marketing"), line: "Siamo nel Marketing: posso proporti campagne, bozze newsletter o link tracciati." },
  { match: (p) => p === "/web" || p === "/monitor/siti", line: "Siamo su Siti: chiedimi come sta andando la SEO questo mese." },
  { match: (p) => p === "/webapp" || p === "/monitor/app", line: "Qui c'è la salute dei collegamenti: chiedimi cosa significa ogni indicatore." },
  { match: (p) => p === "/analisi", line: "Qui ci sono i KPI delle app: posso analizzarli e dirti cosa conta." },
  { match: (p) => p === "/cassaforte", line: "Cassaforte: posso dirti cosa è in scadenza — i valori restano cifrati, non li vedo neanche io." },
  { match: (p) => p === "/admin", line: "Gestione utenti: chiedimi chi ha accesso a cosa o gli ultimi accessi." },
  { match: (p) => p.startsWith("/impostazioni"), line: "Impostazioni: posso controllare l'SMTP o aiutarti con le notifiche." },
];

function birthdayLineForToday(now, firstName) {
  const d = now.getDate();
  const m = now.getMonth() + 1;
  const who = d === 5 && m === 12 ? { n: "Roberto", y: 1970 } : d === 12 && m === 5 ? { n: "Michele", y: 1976 } : null;
  if (!who) return "";
  const age = now.getFullYear() - who.y;
  const isSelf = (firstName || "").trim().toLowerCase() === who.n.toLowerCase();
  return isSelf
    ? `Oggi è un giorno speciale: tanti auguri di buon compleanno! ${age} anni portati benissimo. 🎂`
    : `Oggi è il compleanno di ${who.n} (${age} anni): ricordati di fargli gli auguri! 🎂`;
}

function greetingForNow(firstName, path = "") {
  const now = new Date();
  const name = firstName ? ` ${firstName}` : "";
  const saluto = now.getHours() < 17 ? `Buongiorno${name}!` : `Buonasera${name}!`;
  const bday = birthdayLineForToday(now, firstName);
  if (bday) return `${saluto} ${bday}`;
  const hello = PAGE_HELLO.find((x) => x.match(path || ""));
  const base = hello ? hello.line : "Chiedimi qualsiasi cosa sull'ecosistema: funzioni, dati, potenzialità.";
  return `${saluto} ${base}`;
}

function useViewport() {
  // Autoadattamento al dispositivo: il widget cambia layout in base alla
  // larghezza reale dello schermo (telefono = foglio a tutto schermo,
  // tablet = pannello largo, desktop = pannello ancorato al pulsante).
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return { isMobile: w <= 640, isTablet: w > 640 && w <= 1024 };
}

const deviceKind = () => (window.innerWidth <= 640 ? "mobile" : window.innerWidth <= 1024 ? "tablet" : "desktop");

export default function FloatingViki({ standalone = false }) {
  const location = useLocation();
  const [open, setOpen] = useState(() => standalone || new URLSearchParams(window.location.search).get("viki") === "open");
  const [waving, setWaving] = useState(false);
  const [soundsOn, setSoundsOn] = useState(() => vikiSoundsEnabled());
  const [recState, setRecState] = useState("idle"); // idle | recording | transcribing
  const recorderRef = useRef(null);
  const [handsFree, setHandsFree] = useState(false);
  const [voiceOverlay, setVoiceOverlay] = useState(false);
  const handsFreeRef = useRef(false);
  const hfStreamRef = useRef(null);
  const hfCtxRef = useRef(null);
  const wakeLockRef = useRef(null);

  const acquireWakeLock = async () => {
    try { wakeLockRef.current = await navigator.wakeLock?.request("screen"); } catch { /* wake lock non supportato */ }
  };

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && handsFreeRef.current) acquireWakeLock();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const stopHandsFree = () => {
    handsFreeRef.current = false;
    setHandsFree(false);
    setVoiceOverlay(false);
    try { wakeLockRef.current?.release(); } catch { /* già rilasciato */ }
    wakeLockRef.current = null;
    try { if (recorderRef.current?.state === "recording") recorderRef.current.stop(); } catch { /* già fermo */ }
    hfStreamRef.current?.getTracks().forEach((t) => t.stop());
    hfStreamRef.current = null;
    hfCtxRef.current?.close().catch(() => {});
    hfCtxRef.current = null;
    stopVikiSpeech();
    setRecState("idle");
  };

  const listenOnce = async () => {
    if (!handsFreeRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      hfStreamRef.current = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      const actx = new AC();
      hfCtxRef.current = actx;
      const analyser = actx.createAnalyser();
      analyser.fftSize = 2048;
      actx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      // Rilevamento del silenzio: quando l'utente ha parlato e poi tace
      // per ~1.7s, la registrazione si chiude e parte da sola.
      let spoke = false;
      let lastVoice = Date.now();
      const started = Date.now();
      const SILENCE_MS = 1700, MAX_MS = 30000, IDLE_MS = 12000, THRESH = 0.02;
      const timer = setInterval(() => {
        if (!handsFreeRef.current) { clearInterval(timer); try { rec.stop(); } catch { /* già fermo */ } return; }
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = Date.now();
        if (rms > THRESH) { spoke = true; lastVoice = now; }
        if ((spoke && now - lastVoice > SILENCE_MS) || now - started > MAX_MS || (!spoke && now - started > IDLE_MS)) {
          clearInterval(timer);
          try { rec.stop(); } catch { /* già fermo */ }
        }
      }, 120);
      rec.onstop = async () => {
        clearInterval(timer);
        stream.getTracks().forEach((t) => t.stop());
        actx.close().catch(() => {});
        hfStreamRef.current = null;
        hfCtxRef.current = null;
        if (!handsFreeRef.current) { setRecState("idle"); return; }
        if (!spoke) { listenOnce(); return; } // nessuna voce: torna in ascolto
        setRecState("transcribing");
        let text = "";
        try {
          const blob = new Blob(chunks, { type: mime || "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "voce.webm");
          const { data } = await api.post("/viki/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          text = (data.text || "").trim();
        } catch { text = ""; }
        setRecState("idle");
        if (!handsFreeRef.current) return;
        if (text.length > 1) await sendMessage(text, { spoken: true });
        if (handsFreeRef.current) listenOnce();
      };
      rec.start();
      setRecState("recording");
    } catch {
      stopHandsFree();
      setLog((cur) => [...cur, { from: "viki", text: "Non riesco ad accedere al microfono per la modalità mani libere: controlla i permessi del browser." }]);
    }
  };

  const toggleHandsFree = () => {
    if (handsFree) { stopHandsFree(); return; }
    handsFreeRef.current = true;
    setHandsFree(true);
    setVoiceOverlay(window.innerWidth <= 820);
    acquireWakeLock();
    playVikiNotify();
    listenOnce();
  };

  const toggleDictation = async () => {
    if (recState === "transcribing") return;
    if (recState === "recording") {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecState("transcribing");
        try {
          const blob = new Blob(chunks, { type: mime || "audio/webm" });
          const fd = new FormData();
          fd.append("audio", blob, "voce.webm");
          const { data } = await api.post("/viki/stt", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) {
            setDraft((cur) => (cur ? `${cur} ${data.text}` : data.text));
          }
        } catch {
          setLog((cur) => [...cur, { from: "viki", text: "Non sono riuscita a capire l'audio. Riprova parlando più vicino al microfono." }]);
        } finally {
          setRecState("idle");
          setTimeout(() => inputRef.current?.focus(), 50);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecState("recording");
    } catch {
      setLog((cur) => [...cur, { from: "viki", text: "Non riesco ad accedere al microfono: controlla i permessi del browser." }]);
    }
  };
  // Il nome arriva dalla stessa chiamata /viki/access-check già fatta per
  // decidere se mostrarsi — non da un meccanismo diverso per ciascuna
  // app: Posta AI e Tesoreria non hanno un useAuth() di AREAPAQ a
  // disposizione, quindi tutti e tre i widget leggono il nome da qui,
  // per coerenza.
  const [firstName, setFirstName] = useState("");
  const [privileged, setPrivileged] = useState(true);
  const [role, setRole] = useState("");

  // VIKI non deve comparire affatto per chi non ha i requisiti: nessuna
  // icona, nessun pulsante, nessun indizio che esista. Finché non
  // sappiamo con certezza che l'utente è autorizzato, non renderizziamo
  // nulla — "null" resta lo stato di partenza, mai un default ottimista.
  const [authorized, setAuthorized] = useState(null); // null = non ancora verificato

  useEffect(() => {
    let cancelled = false;
    api.get("/viki/access-check")
      .then(({ data }) => {
        if (cancelled) return;
        setAuthorized(Boolean(data?.authorized));
        if (data?.first_name) setFirstName(data.first_name);
        setPrivileged(["master", "admin"].includes(data?.role));
        setRole(data?.role || "");
      })
      .catch(() => { if (!cancelled) setAuthorized(false); });
    return () => { cancelled = true; };
  }, []);

  const [pos, setPos] = useState(() => {
    const safe = (p) => ({
      x: Math.min(Math.max(8, Number(p.x) || 0), window.innerWidth - 100),
      y: Math.min(Math.max(8, Number(p.y) || 0), window.innerHeight - 116),
    });
    try {
      const saved = JSON.parse(localStorage.getItem("viki-widget-pos-v3"));
      if (saved?.x != null) return safe(saved);
    } catch { /* nessuna posizione salvata: usa il default */ }
    return { x: window.innerWidth - 108, y: 72 };
  });
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0, moved: false });

  // se la finestra cambia dimensione, l'icona rientra da sola nello schermo
  useEffect(() => {
    const onResize = () => setPos((p) => ({
      x: Math.min(Math.max(8, p.x), window.innerWidth - 100),
      y: Math.min(Math.max(8, p.y), window.innerHeight - 116),
    }));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // La conversazione resta viva cambiando pagina: session_id e log si
  // leggono da sessionStorage all'avvio e si riscrivono a ogni messaggio,
  // così muoversi tra le pagine dell'app non azzera la chat in corso.
  const sessionId = useRef((() => {
    try {
      const saved = sessionStorage.getItem("viki-session-id");
      if (saved) return saved;
    } catch { /* sessionStorage non disponibile: si genera comunque un id nuovo */ }
    const fresh = `viki-float-${Date.now()}`;
    try { sessionStorage.setItem("viki-session-id", fresh); } catch { /* ignorato */ }
    return fresh;
  })());

  const [log, setLog] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("viki-chat-log"));
      if (Array.isArray(saved) && saved.length) return saved;
    } catch { /* nessuna conversazione salvata: parte dal saluto */ }
    return [{ from: "viki", text: greetingForNow("", location.pathname) }]; // senza nome al primissimo render: corretto sotto appena disponibile
  });

  // Il nome dell'utente arriva in modo asincrono da useAuth(): corregge
  // il saluto iniziale appena disponibile, senza toccare nulla se
  // l'utente ha già iniziato a scrivere (stesso principio già applicato
  // altrove in questo progetto per non sovrascrivere una conversazione
  // in corso con un aggiornamento tardivo).
  useEffect(() => {
    if (!firstName) return;
    setLog((cur) => {
      if (cur.length !== 1 || cur[0].from !== "viki") return cur;
      return [{ from: "viki", text: greetingForNow(firstName, location.pathname) }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName]);
  // Reattività di pagina: se la conversazione è ancora al solo saluto,
  // il saluto si adatta alla pagina in cui l'utente si sposta.
  useEffect(() => {
    setLog((cur) => {
      if (cur.length !== 1 || cur[0].from !== "viki") return cur;
      return [{ from: "viki", text: greetingForNow(firstName, location.pathname) }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [draft, setDraft] = useState("");
  const [attachedImage, setAttachedImage] = useState(null); // { base64, preview }
  const imageInputRef = useRef(null);

  const attachImage = (file) => {
    if (!file || !/image\/(png|jpeg|webp)/.test(file.type)) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1280;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        setAttachedImage({ base64: dataUrl.split(",")[1], preview: dataUrl });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const [sending, setSending] = useState(false);
  const { isMobile, isTablet } = useViewport();
  // Dimensione del riquadro personalizzabile dall'utente (persistita)
  const [sheetH, setSheetH] = useState(() => {
    const v = parseFloat(localStorage.getItem("viki_sheet_h"));
    return v >= 40 && v <= 92 ? v : 75;
  });
  const [panelSize, setPanelSize] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("viki_panel_size"));
      if (v && v.w && v.mh) return v;
    } catch { /* dimensione di default */ }
    return null;
  });
  const resizeRef = useRef(null);
  const [agentMode, setAgentMode] = useState(false); // false = "Chiedi", true = "Obiettivo"
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [attention, setAttention] = useState({ count: 0, items: [] });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const stopAgentRef = useRef(false);
  const agentJobIdRef = useRef(null);
  const panelRef = useRef(null);

  // Badge proattivo: ogni 90s chiede al backend se c'è qualcosa che merita
  // attenzione (promemoria scaduti, decisioni in attesa, database giù).
  useEffect(() => {
    if (!authorized) return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/viki/attention");
        if (!stop) setAttention(data);
      } catch { /* il badge resta com'è: niente allarmi falsi per un errore di rete */ }
    };
    load();
    const t = setInterval(load, 90000);
    return () => { stop = true; clearInterval(t); };
  }, [authorized]);

  // VIKI sorride quando finisce un lavoro: piccolo momento di gioia
  const [happy, setHappy] = useState(false);
  const prevSendingRef = useRef(false);
  useEffect(() => {
    const wasSending = prevSendingRef.current;
    prevSendingRef.current = sending;
    if (wasSending && !sending) {
      setHappy(true);
      const t = setTimeout(() => setHappy(false), 3200);
      return () => clearTimeout(t);
    }
  }, [sending]);

  // Briefing del giorno: alla prima apertura del widget di ogni giornata
  // VIKI porta il quadro reale (generato una volta al giorno, lato server).
  const maybeFetchBriefing = async () => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("viki-briefing-day") === today) return;
    localStorage.setItem("viki-briefing-day", today);
    try {
      const { data } = await api.get("/viki/briefing");
      setLog((cur) => [...cur, { from: "viki", text: `Briefing di oggi\n\n${data.text}`, briefing: true }]);
      playVikiNotify();
    } catch { /* niente briefing: nessun disturbo */ }
  };

  // Notifiche push nella PWA: iscrizione dedicata sulla registrazione con
  // scope /viki-app, stessi endpoint (master-only) già usati dal portale.
  const [pushState, setPushState] = useState("idle"); // idle | on | busy | unsupported
  useEffect(() => {
    if (!standalone) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setPushState("unsupported");
      return;
    }
    navigator.serviceWorker.getRegistration("/viki-app")
      .then((reg) => reg?.pushManager.getSubscription())
      .then((sub) => setPushState(sub ? "on" : "idle"))
      .catch(() => {});
  }, [standalone]);

  const togglePush = async () => {
    if (pushState === "busy" || pushState === "unsupported") return;
    const wasOn = pushState === "on";
    setPushState("busy");
    try {
      const reg = await navigator.serviceWorker.register("/viki-sw.js", { scope: "/viki-app" });
      if (wasOn) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
          await sub.unsubscribe();
        }
        setPushState("idle");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState("idle"); return; }
      const b64ToU8 = (b) => {
        const pad = "=".repeat((4 - (b.length % 4)) % 4);
        const raw = atob((b + pad).replace(/-/g, "+").replace(/_/g, "/"));
        return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
      };
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.key) });
      await api.post("/push/subscribe", sub.toJSON());
      setPushState("on");
    } catch {
      setPushState(wasOn ? "on" : "idle");
    }
  };

  // Pulsanti cuffie/microfono/allegato: nella dock (widget) o nella riga
  // delle modalità (PWA standalone, per lasciare largo il campo di scrittura).
  const renderUtilityBtns = (sz, rad) => (
    <>
      <button onClick={toggleHandsFree} disabled={sending && !handsFree} data-testid="viki-handsfree-button"
        title={handsFree ? "Esci dalla modalità mani libere" : "Modalità mani libere: parla e VIKI risponde, senza toccare nulla"}
        className={`flex items-center justify-center shrink-0 ${handsFree ? "animate-pulse" : ""}`}
        style={{ width: sz, height: sz, borderRadius: rad,
                 background: handsFree ? C.cyanDim : "transparent",
                 color: handsFree ? C.cyan : C.mutedDim,
                 border: `1px solid ${handsFree ? C.cyan + "66" : C.border}` }}>
        <Headphones size={14} />
      </button>
      <button onClick={toggleDictation} disabled={sending || recState === "transcribing" || handsFree} data-testid="viki-mic-button"
        title={recState === "recording" ? "Ferma la registrazione" : "Detta il messaggio a voce"}
        className={`flex items-center justify-center shrink-0 ${recState === "recording" ? "animate-pulse" : ""}`}
        style={{ width: sz, height: sz, borderRadius: rad,
                 background: recState === "recording" ? "rgba(224,97,111,0.18)" : recState === "transcribing" ? C.cyanDim : "transparent",
                 color: recState === "recording" ? "#E0616F" : C.mutedDim,
                 border: `1px solid ${recState === "recording" ? "#E0616F66" : C.border}` }}>
        {recState === "recording" ? <Square size={12} /> : <Mic size={14} className={recState === "transcribing" ? "animate-pulse" : ""} style={recState === "transcribing" ? { color: C.cyan } : undefined} />}
      </button>
      {!agentMode && (
        <button onClick={() => imageInputRef.current?.click()} disabled={sending} title="Allega un'immagine (VIKI la legge e la commenta)"
          data-testid="viki-attach-image-btn"
          className="flex items-center justify-center shrink-0"
          style={{ width: sz, height: sz, borderRadius: rad,
                   background: attachedImage ? C.cyanDim : "transparent",
                   color: attachedImage ? C.cyan : C.mutedDim,
                   border: `1px solid ${attachedImage ? C.cyan + "66" : C.border}` }}>
          <Paperclip size={14} />
        </button>
      )}
    </>
  );

  // Ctrl+K / ⌘K: apre e chiude VIKI da qualsiasi pagina
  useEffect(() => {
    if (standalone) return; // PWA: il pannello è sempre aperto, niente toggle
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => {
          if (!o) maybeFetchBriefing();
          return !o;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("viki-widget-pos-v3", JSON.stringify(pos));
  }, [pos]);

  useEffect(() => {
    try { sessionStorage.setItem("viki-chat-log", JSON.stringify(log.filter((l) => l.from !== "viki-stream"))); } catch { /* ignorato */ }
  }, [log]);

  // Scroll automatico all'ultimo messaggio, anche durante la digitazione
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [log, sending]);

  // Focus automatico sulla casella quando si apre il pannello
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  // L'area di testo si espande da sola man mano che si scrive, fino a un
  // tetto oltre il quale scorre — niente altezza fissa che tronca la vista
  // di un obiettivo lungo per la modalità Agente.
  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = "auto";
    inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 160) + "px";
  }, [draft]);

  // Esc per chiudere, click fuori dal pannello per chiudere
  useEffect(() => {
    if (!open || standalone) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [open]);

  const clampPos = (x, y) => ({
    x: Math.min(Math.max(8, x), window.innerWidth - 100),
    y: Math.min(Math.max(8, y), window.innerHeight - 116),
  });

  const startDrag = (clientX, clientY) => {
    dragRef.current = { dragging: true, offsetX: clientX - pos.x, offsetY: clientY - pos.y, moved: false };
  };
  const onMove = useCallback((clientX, clientY) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.moved = true;
    setPos(clampPos(clientX - dragRef.current.offsetX, clientY - dragRef.current.offsetY));
  }, []);
  const endDrag = () => { dragRef.current.dragging = false; };

  // ----- Ridimensionamento del riquadro -----
  const startResize = (clientX, clientY) => {
    if (isMobile) {
      resizeRef.current = { mobile: true };
      return;
    }
    const rect = panelRef.current?.getBoundingClientRect();
    const defMh = Math.min(window.innerHeight * (isTablet ? 0.46 : 0.48), isTablet ? 420 : 440);
    resizeRef.current = {
      startX: clientX, startY: clientY,
      startW: rect ? rect.width : (isTablet ? 400 : 384),
      startH: panelSize?.mh || defMh,
      gripTop: panelAbove, gripLeft: panelSide === "right",
    };
  };
  const onResizeMove = useCallback((clientX, clientY) => {
    const r = resizeRef.current;
    if (!r) return;
    if (r.mobile) {
      setSheetH(Math.min(92, Math.max(40, ((window.innerHeight - clientY) / window.innerHeight) * 100)));
      return;
    }
    const dw = r.gripLeft ? r.startX - clientX : clientX - r.startX;
    const dh = r.gripTop ? r.startY - clientY : clientY - r.startY;
    setPanelSize({
      w: Math.min(620, Math.max(320, r.startW + dw)),
      mh: Math.min(window.innerHeight * 0.75, Math.max(240, r.startH + dh)),
    });
  }, []);
  useEffect(() => { localStorage.setItem("viki_sheet_h", String(Math.round(sheetH))); }, [sheetH]);
  useEffect(() => {
    if (panelSize) {
      localStorage.setItem("viki_panel_size", JSON.stringify({ w: Math.round(panelSize.w), mh: Math.round(panelSize.mh) }));
    } else {
      localStorage.removeItem("viki_panel_size");
    }
  }, [panelSize]);
  useEffect(() => {
    const mm = (e) => onResizeMove(e.clientX, e.clientY);
    const mu = () => { resizeRef.current = null; };
    const tm = (e) => {
      if (!resizeRef.current || !e.touches[0]) return;
      e.preventDefault();
      onResizeMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);
    window.addEventListener("touchmove", tm, { passive: false });
    window.addEventListener("touchend", mu);
    window.addEventListener("touchcancel", mu);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", mu);
      window.removeEventListener("touchcancel", mu);
    };
  }, [onResizeMove]);

  useEffect(() => {
    const mm = (e) => onMove(e.clientX, e.clientY);
    const mu = () => endDrag();
    // passive: false + preventDefault SOLO mentre si trascina: senza, su
    // smartphone/tablet il drag combatte con lo scroll della pagina e il
    // pulsante "scappa" — era questa la difficoltà nel farlo flottare.
    const tm = (e) => {
      if (!dragRef.current.dragging || !e.touches[0]) return;
      e.preventDefault();
      onMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", mu);
    window.addEventListener("touchmove", tm, { passive: false });
    window.addEventListener("touchend", mu);
    window.addEventListener("touchcancel", mu);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", mu);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", mu);
      window.removeEventListener("touchcancel", mu);
    };
  }, [onMove]);

  const handleButtonClick = () => {
    if (dragRef.current.moved) return; // era un trascinamento, non un click
    setOpen((o) => {
      if (!o) {
        setWaving(true);
        setTimeout(() => setWaving(false), 2200);
        maybeFetchBriefing();
      }
      return !o;
    });
  };

  const newConversation = () => {
    stopVikiSpeech();
    const fresh = `viki-float-${Date.now()}`;
    sessionId.current = fresh;
    try {
      sessionStorage.setItem("viki-session-id", fresh);
      sessionStorage.removeItem("viki-chat-log");
    } catch { /* sessionStorage non disponibile */ }
    setLog([{ from: "viki", text: greetingForNow(firstName, location.pathname) }]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 1500);
    } catch {
      /* clipboard non disponibile: nessuna azione, il testo resta comunque selezionabile a mano */
    }
  };

  const runRecognition = async () => {
    // Un'azione istantanea, non una modalità che aspetta di scrivere
    // qualcosa: un clic e parte subito. Stesso identico calcolo già
    // usato in VikiPage.js (VikiChatCard) — decisioni in attesa da
    // /viki/decision-log, funzioni e moduli da /viki/data — non numeri
    // di esempio scritti a mano.
    if (sending) return;
    setSending(true);
    try {
      const [decisionRes, fnRes, integrRes, briefRes] = await Promise.all([
        api.get("/viki/decision-log"),
        api.get("/viki/data/viki-areapaq-functions").catch(() => ({ data: { value: null } })),
        api.get("/viki/data/viki-areapaq-integrations").catch(() => ({ data: { value: null } })),
        api.get("/viki/briefing").catch(() => ({ data: { text: "" } })),
      ]);
      const pending = (decisionRes.data.decisions || []).filter((d) => d.status === "In attesa").length;
      const fns = fnRes.data.value || [];
      const integr = integrRes.data.value || [];
      const fnPending = fns.filter((f) => f.status !== "Appresa").length;
      const toConnect = integr.filter((i) => i.status !== "Connesso").length;
      const brief = (briefRes.data.text || "").trim();
      const recap = `Mentre eri via: ${pending} decision${pending === 1 ? "e" : "i"} in attesa della tua conferma, ${fnPending} funzion${fnPending === 1 ? "e" : "i"} ancora da verificare e ${toConnect} moduli PAQ da collegare.`;
      const text = `${brief ? `Briefing di oggi\n\n${brief}\n\n` : ""}Bentornato${firstName ? `, ${firstName}` : ""}. ${recap}`;
      playVikiNotify();
      setLog((cur) => [...cur, { from: "viki", text }]);
    } catch {
      setLog((cur) => [...cur, { from: "viki", text: "Non sono riuscita a fare il briefing in questo momento. Riprova tra poco." }]);
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async (text, { spoken = false } = {}) => {
    const img = attachedImage;
    setAttachedImage(null);
    setLog((cur) => [...cur, { from: "user", text, imagePreview: img?.preview || null }]);
    setSending(true);
    const reqBody = {
      text,
      image_base64: img?.base64 || undefined,
      session_id: sessionId.current,
      app_name: APP_KEY,
      page_path: location.pathname,
      page_label: document.title || location.pathname,
      client_time: new Date().toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" }),
      device: deviceKind(),
      spoken,
    };
    try {
      // Prima scelta: streaming — il testo compare mentre VIKI scrive.
      let payload = null;
      try {
        payload = await streamVikiChat(reqBody, {
          onDelta: (c) => setLog((cur) => {
            const last = cur[cur.length - 1];
            if (last?.from === "viki-stream") return [...cur.slice(0, -1), { ...last, text: last.text + c }];
            return [...cur, { from: "viki-stream", text: c }];
          }),
        });
      } catch { payload = null; /* streaming non disponibile: si ripiega sulla chiamata classica */ }
      setLog((cur) => cur.filter((l) => l.from !== "viki-stream"));
      if (!payload) {
        const { data } = await api.post("/viki/chat", reqBody);
        payload = data;
      }

      const statusLabel = TWO_PHASE_STATUS_LABELS[payload.status];
      if (statusLabel) {
        // Primo passaggio visibile: VIKI dice cosa sta facendo, poi
        // completa da sola — l'utente vede accadere i due passi invece
        // di aspettare al buio un'unica risposta lenta.
        setLog((cur) => [...cur, { from: "viki-status", text: statusLabel(payload) }]);
        const { data: final } = await api.post("/viki/chat/continue", { session_id: sessionId.current, spoken });
        payload = final;
      }
      playVikiReply();
      setLog((cur) => [...cur, { from: "viki", text: payload.reply, action: payload.proposed_action || null, actions: payload.proposed_actions || null, sources: payload.sources || null, followups: payload.followups || null }]);
      if (spoken) {
        // mani libere (UNICO percorso con sintesi vocale): aspetta che VIKI
        // finisca di parlare prima di riaprire il microfono
        await speakViki(payload.reply, { waitEnd: true });
      }
    } catch {
      setLog((cur) => [...cur.filter((l) => l.from !== "viki-stream"), { from: "viki", text: "Non sono riuscita a rispondere in questo momento. Riprova tra poco." }]);
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    if (!draft.trim() || sending) return;
    const text = draft.trim();
    setDraft("");
    await sendMessage(text);
  };

  const AGENT_LOG_LABELS = { search: "🔎", crossapp: "📊", action: "📝" };

  const runAgent = async () => {
    if (!draft.trim() || sending) return;
    const goal = draft.trim();
    stopAgentRef.current = false;
    setLog((cur) => [...cur, { from: "user", text: goal, isGoal: true }]);
    setDraft("");
    setSending(true);
    let shownLogCount = 0;
    try {
      const { data } = await api.post("/viki/agent/start", {
        goal,
        app_name: APP_KEY,
        page_path: location.pathname,
      });
      const jobId = data.job_id;
      agentJobIdRef.current = jobId;

      // Polling: il lavoro gira in background sul server, qui si
      // controlla lo stato periodicamente invece di tenere aperta
      // un'unica richiesta che rischierebbe il timeout su obiettivi
      // lunghi (più ricerche, più proposte in sequenza).
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (stopAgentRef.current) {
          setLog((cur) => [...cur, {
            from: "viki",
            text: "Ho chiesto al server di fermare il lavoro: nessun altro passo verrà eseguito.",
          }]);
          break;
        }
        await new Promise((r) => setTimeout(r, 1500));
        const { data: status } = await api.get(`/viki/agent/status/${jobId}`);

        if (status.log.length > shownLogCount) {
          const newEntries = status.log.slice(shownLogCount);
          shownLogCount = status.log.length;
          setLog((cur) => [...cur, ...newEntries.map((e) => ({ from: "viki-status", text: `${AGENT_LOG_LABELS[e.type] || ""} ${e.text}` }))]);
        }

        if (status.status === "stopped") {
          setLog((cur) => [...cur, { from: "viki", text: status.summary || "Lavoro interrotto su tua richiesta." }]);
          break;
        }
        if (status.status === "done") {
          playVikiNotify();
          setLog((cur) => [...cur, {
            from: "viki",
            text: status.summary || "Obiettivo completato.",
            agentActions: status.actions && status.actions.length > 0 ? status.actions : null,
          }]);
          break;
        }
        if (status.status === "error") {
          setLog((cur) => [...cur, { from: "viki", text: `Il lavoro si è interrotto: ${status.error || "errore sconosciuto"}.` }]);
          break;
        }
      }
    } catch (err) {
      const limitMsg = err?.response?.status === 429
        ? "Ho raggiunto il limite giornaliero di lavori in modalità Agente: riprova domani, o passa alla modalità Chiedi."
        : "Non sono riuscita ad avviare il lavoro. Riprova tra poco.";
      setLog((cur) => [...cur, { from: "viki", text: limitMsg }]);
    } finally {
      setSending(false);
    }
  };

  const panelSide = pos.x > window.innerWidth / 2 ? "right" : "left";
  const panelAbove = pos.y > window.innerHeight / 2;

  // Nessuna icona, nessun pulsante: per chi non ha i requisiti VIKI è
  // come se non esistesse in questa app. L'eventuale altra AI già
  // presente resta al suo posto, invariata.
  if (!authorized) return null;

  return (
    <>
      {/* modalità vocale a schermo intero (mobile): pulsantoni, stato grande,
          schermo tenuto acceso con Wake Lock finché la modalità è attiva */}
      {handsFree && voiceOverlay && (() => {
        const phase = recState === "recording" ? "listening" : recState === "transcribing" ? "transcribing" : sending ? "speaking" : "idle";
        const phaseText = {
          listening: "Ti ascolto… parla pure",
          transcribing: "Sto trascrivendo…",
          speaking: "V.I.K.I. sta rispondendo…",
          idle: "Un attimo…",
        }[phase];
        const lastReply = [...log].reverse().find((l) => l.from === "viki")?.text || "";
        return (
          <div data-testid="viki-voice-overlay" style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "radial-gradient(circle at 50% 28%, #10222C 0%, #070B10 72%)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between",
            padding: "40px 24px 44px",
          }}>
            <span style={{ color: C.cyan, fontSize: 13, fontWeight: 800, letterSpacing: 3 }}>V.I.K.I. · MANI LIBERE</span>
            <div className="flex flex-col items-center gap-6" style={{ maxWidth: 420 }}>
              <div style={{ position: "relative", width: 190, height: 190, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {phase === "listening" && (
                  <>
                    <span className="animate-ping" style={{ position: "absolute", inset: 8, borderRadius: "50%", border: `2px solid ${C.cyan}55` }} />
                    <span style={{ position: "absolute", inset: -10, borderRadius: "50%", border: `1.5px solid ${C.cyan}33` }} />
                  </>
                )}
                <VikiAvatar size={150} mood={phase === "listening" ? "idle" : "thinking"} theme={APP_THEME} float glow />
              </div>
              <span data-testid="viki-voice-phase" style={{ color: C.text, fontSize: 19, fontWeight: 700, textAlign: "center" }}>{phaseText}</span>
              {lastReply && (
                <p style={{
                  color: C.muted, fontSize: 14, lineHeight: 1.55, textAlign: "center", margin: 0,
                  display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>{lastReply}</p>
              )}
            </div>
            <div className="flex flex-col items-center gap-2.5">
              <button onClick={stopHandsFree} data-testid="viki-voice-overlay-stop"
                style={{
                  width: 84, height: 84, borderRadius: "50%",
                  background: "rgba(224,97,111,0.16)", border: "2px solid #E0616F",
                  color: "#E0616F", display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 0 30px rgba(224,97,111,0.25)",
                }}>
                <Square size={30} />
              </button>
              <span style={{ color: C.mutedDim, fontSize: 12.5 }}>Termina la conversazione</span>
            </div>
          </div>
        );
      })()}
      {/* pulsante flottante trascinabile — cerchio compatto con mascotte */}
      <style>{`
        @keyframes vikiPulseRing { 0% { transform: scale(1); opacity: 0.55; } 100% { transform: scale(1.55); opacity: 0; } }
        @keyframes vikiBreathe { 0%, 100% { box-shadow: 0 0 10px rgba(69,224,208,0.35), 0 6px 16px rgba(0,0,0,0.5); } 50% { box-shadow: 0 0 20px rgba(69,224,208,0.65), 0 6px 16px rgba(0,0,0,0.5); } }
        @keyframes vikiDotBounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.4; } 40% { transform: translateY(-3px); opacity: 1; } }
        @keyframes vikiCaretBlink { 50% { opacity: 0; } }
        @keyframes vikiPanelIn { from { opacity: 0; transform: translateY(14px) scale(0.97); } to { opacity: 1; transform: none; } }
        @keyframes vikiSheetIn { from { transform: translateY(45%); opacity: 0.4; } to { transform: none; opacity: 1; } }
        @keyframes vikiMsgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .viki-panel-in { animation: vikiPanelIn 0.28s cubic-bezier(0.2, 0.9, 0.3, 1); }
        .viki-sheet-in { animation: vikiSheetIn 0.32s cubic-bezier(0.2, 0.9, 0.3, 1); }
        .viki-msg-in { animation: vikiMsgIn 0.25s ease-out; }
        .viki-panel button { transition: transform 0.15s ease, background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease; }
        .viki-panel button:hover:not(:disabled) { transform: translateY(-1px); }
        .viki-panel button:active:not(:disabled) { transform: translateY(0) scale(0.97); }
        .viki-scroll::-webkit-scrollbar { width: 5px; }
        .viki-scroll::-webkit-scrollbar-track { background: transparent; }
        .viki-scroll::-webkit-scrollbar-thumb { background: rgba(69,224,208,0.22); border-radius: 4px; }
        .viki-scroll::-webkit-scrollbar-thumb:hover { background: rgba(69,224,208,0.4); }
        .viki-input:focus { border-color: rgba(69,224,208,0.55) !important; box-shadow: 0 0 0 3px rgba(69,224,208,0.10); outline: none; }
        .viki-fab-ring { animation: vikiPulseRing 2.6s ease-out infinite; }
        .viki-fab-circle { transition: transform 0.2s ease; filter: drop-shadow(0 1px 3px rgba(8,12,18,0.85)) drop-shadow(0 0 10px rgba(8,12,18,0.55)) drop-shadow(0 0 24px rgba(148,163,184,0.35)); }
        .viki-fab-circle:hover { transform: scale(1.08); }
        .viki-dot { animation: vikiDotBounce 1.2s ease-in-out infinite; }
        .viki-caret { display: inline-block; width: 7px; height: 13px; margin-left: 3px; background: #45E0D0; vertical-align: -2px; animation: vikiCaretBlink 0.8s steps(1) infinite; border-radius: 1px; }
        .viki-sa-actions button { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.09); display: flex; align-items: center; justify-content: center; transition: background-color 0.2s ease, transform 0.15s ease; }
        .viki-sa-actions button:active { transform: scale(0.9); }
        .viki-sa-card { transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease; }
        .viki-sa-card:hover { background: rgba(69,224,208,0.10) !important; border-color: rgba(69,224,208,0.45) !important; transform: translateY(-1px); }
        .viki-sa-card:active { transform: scale(0.98); }
        .viki-sa-float { animation: vikiSaFloat 3.6s ease-in-out infinite; }
        .viki-sa-halo { position: absolute; inset: -12px; border-radius: 50%; background: radial-gradient(circle, rgba(69,224,208,0.22) 0%, rgba(156,143,240,0.10) 50%, transparent 75%); animation: vikiSaGlow 3.6s ease-in-out infinite; }
        @keyframes vikiSaFloat { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-7px) rotate(1deg); } }
        @keyframes vikiSaGlow { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.07); } }
      `}</style>
      {!standalone && (
      <div
        data-testid="viki-floating-button"
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onTouchStart={(e) => e.touches[0] && startDrag(e.touches[0].clientX, e.touches[0].clientY)}
        onClick={handleButtonClick}
        title="V.I.K.I. — Ctrl+K per aprire"
        style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 9999, cursor: "grab", userSelect: "none", touchAction: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}
      >
        <div style={{ position: "relative", width: 92, height: 92 }}>
          <div className="viki-fab-circle" style={{
            width: 92, height: 92, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <VikiAvatar size={90} mood={sending ? "thinking" : happy ? "happy" : open ? "wave" : "idle"} theme={APP_THEME} />
          </div>
          {APP_BADGE && !APP_THEME && (
            <span data-testid="viki-app-badge" style={{
              position: "absolute", bottom: -3, right: -3, width: 20, height: 20, borderRadius: "50%",
              background: C.cyan, color: "#0A0E13", display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #0A0E13", boxShadow: "0 0 8px rgba(69,224,208,0.55)",
            }}><APP_BADGE.Icon size={11} strokeWidth={2.6} /></span>
          )}
          {attention.count > 0 && !open && (
            <span data-testid="viki-attention-badge" className="animate-pulse" style={{
              position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, padding: "0 4px",
              borderRadius: 9, background: "#E0616F", color: "#fff", fontSize: 10.5, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #0A0E13", boxShadow: "0 0 8px rgba(224,97,111,0.6)",
            }}>{attention.count > 9 ? "9+" : attention.count}</span>
          )}
        </div>
        <span style={{ color: C.text, fontSize: 9.5, fontWeight: 800, letterSpacing: 1.5, textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>V.I.K.I.</span>
      </div>
      )}

      {/* pannello chat, ancorato al pulsante */}
      {open && (
        <div
          ref={panelRef}
          className={standalone ? "viki-standalone viki-panel" : isMobile ? "viki-sheet-in viki-panel" : "viki-panel-in viki-panel"}
          style={standalone ? {
            position: "fixed", inset: 0, zIndex: 9999, width: "100%", height: "100dvh",
            background: "#080C11", display: "flex", flexDirection: "column", overflow: "hidden",
            paddingBottom: "env(safe-area-inset-bottom)",
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
          } : isMobile ? {
            position: "fixed", zIndex: 9999, left: 0, right: 0, bottom: 0,
            width: "100%", maxWidth: "100%", height: `${sheetH}dvh`,
            background: C.surface, borderTop: "1px solid rgba(69,224,208,0.3)",
            borderRadius: "18px 18px 0 0",
            boxShadow: "0 -20px 50px -12px rgba(0,0,0,0.7), 0 0 28px rgba(69,224,208,0.10)",
            display: "flex", flexDirection: "column", overflow: "hidden",
            paddingBottom: "env(safe-area-inset-bottom)",
          } : {
            position: "fixed", zIndex: 9999, width: panelSize?.w || (isTablet ? 400 : 384), maxWidth: "calc(100vw - 24px)",
            left: panelSide === "left" ? Math.min(pos.x, window.innerWidth - ((panelSize?.w || (isTablet ? 400 : 384)) + 12)) : undefined,
            right: panelSide === "right" ? Math.max(12, window.innerWidth - pos.x - 92) : undefined,
            top: panelAbove ? undefined : Math.min(pos.y + 114, window.innerHeight - 420),
            bottom: panelAbove ? Math.max(12, window.innerHeight - pos.y + 10) : undefined,
            background: C.surface, border: "1px solid rgba(69,224,208,0.26)", borderRadius: 16,
            boxShadow: "0 20px 60px -12px rgba(0,0,0,0.75), 0 0 24px rgba(69,224,208,0.10)",
            display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          <div style={{ height: 2.5, flexShrink: 0, background: "linear-gradient(90deg, #45E0D0 0%, #9C8FF0 55%, transparent 100%)" }} />
          {standalone && (
            <>
              <div style={{ position: "absolute", top: -120, right: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(69,224,208,0.10) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
              <div style={{ position: "absolute", bottom: -140, left: -120, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle, rgba(156,143,240,0.10) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
            </>
          )}
          {standalone ? null : isMobile ? (
            <div data-testid="viki-resize-handle-mobile"
              onTouchStart={(e) => { if (e.touches[0]) startResize(e.touches[0].clientX, e.touches[0].clientY); }}
              onMouseDown={(e) => { e.preventDefault(); startResize(e.clientX, e.clientY); }}
              onDoubleClick={() => setSheetH(75)}
              style={{ padding: "7px 0 3px", display: "flex", justifyContent: "center", cursor: "ns-resize", touchAction: "none", flexShrink: 0 }}>
              <div style={{ width: 46, height: 4.5, borderRadius: 999, background: "rgba(255,255,255,0.22)" }} />
            </div>
          ) : (
            <div data-testid="viki-resize-handle"
              onMouseDown={(e) => { e.preventDefault(); startResize(e.clientX, e.clientY); }}
              onTouchStart={(e) => e.touches[0] && startResize(e.touches[0].clientX, e.touches[0].clientY)}
              onDoubleClick={() => setPanelSize(null)}
              title="Trascina per ridimensionare · doppio clic per ripristinare"
              style={{
                position: "absolute", width: 20, height: 20, zIndex: 5,
                [panelAbove ? "top" : "bottom"]: 0,
                [panelSide === "right" ? "left" : "right"]: 0,
                cursor: panelAbove === (panelSide === "right") ? "nwse-resize" : "nesw-resize",
                touchAction: "none",
              }}>
              <div style={{
                position: "absolute", width: 9, height: 9,
                [panelAbove ? "top" : "bottom"]: 5,
                [panelSide === "right" ? "left" : "right"]: 5,
                [panelAbove ? "borderTop" : "borderBottom"]: "2px solid rgba(69,224,208,0.45)",
                [panelSide === "right" ? "borderLeft" : "borderRight"]: "2px solid rgba(69,224,208,0.45)",
                borderRadius: 2,
              }} />
            </div>
          )}
          <div className="flex items-center justify-between px-3.5 pt-2.5 pb-2" style={standalone
            ? { padding: "12px 16px", borderBottom: "1px solid rgba(69,224,208,0.15)", background: "rgba(10,14,20,0.85)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", position: "relative", zIndex: 2, flexShrink: 0 }
            : { borderBottom: `1px solid ${C.border}`, background: "linear-gradient(180deg, rgba(69,224,208,0.10) 0%, rgba(156,143,240,0.05) 60%, transparent 100%)" }}>
            <div className="flex items-center gap-2.5">
              <VikiAvatar size={standalone ? 44 : 34} mood={sending ? "thinking" : happy ? "happy" : waving ? "wave" : "idle"} theme={APP_THEME} float={!sending} />
              <div className="flex flex-col">
                <span style={{ color: C.text, fontSize: standalone ? 16.5 : 12.5, fontWeight: standalone ? 800 : 700, letterSpacing: standalone ? -0.2 : 0.4 }}>V.I.K.I.</span>
                <span className="flex items-center gap-1" style={{ color: C.mutedDim, fontSize: standalone ? 11 : 9.5 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: sending ? "#F2A93B" : "#34d399", boxShadow: `0 0 5px ${sending ? "#F2A93B" : "#34d399"}` }} className="animate-pulse" />
                  {sending ? "sto lavorando…" : (APP_BADGE ? APP_BADGE.flavor : "Assistente AI")}
                </span>
              </div>
            </div>
            <div className={standalone ? "viki-sa-actions flex items-center gap-2" : "flex items-center gap-1"}>
              {standalone && role === "master" && pushState !== "unsupported" && (
                <button onClick={togglePush} data-testid="viki-pwa-push-toggle" disabled={pushState === "busy"}
                  title={pushState === "on" ? "Notifiche push attive su questo dispositivo — tocca per disattivare" : "Attiva le notifiche push su questo dispositivo"}
                  style={{ color: pushState === "on" ? C.cyan : C.mutedDim }}>
                  {pushState === "on" ? <BellRing size={14} /> : <Bell size={14} />}
                </button>
              )}
              <button onClick={() => { setVikiSoundsEnabled(!soundsOn); setSoundsOn(!soundsOn); }}
                data-testid="viki-sound-toggle" title={soundsOn ? "Disattiva suoni" : "Attiva suoni"}
                style={{ color: soundsOn ? C.cyan : C.mutedDim }}>
                {soundsOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              <button onClick={newConversation} data-testid="viki-new-conversation" title="Nuova conversazione"
                style={{ color: C.mutedDim }}>
                <RotateCcw size={13} />
              </button>
              {!standalone && (
                <button onClick={() => { stopHandsFree(); setOpen(false); }} style={{ color: C.mutedDim }}><X size={16} /></button>
              )}
            </div>
          </div>
          <div style={{ color: C.mutedDim, fontSize: 11, padding: "6px 14px 0" }}>
            Pagina attuale: <span style={{ color: C.muted }}>{location.pathname}</span>
          </div>
          <div className="flex flex-col gap-2.5 viki-scroll" style={standalone
            ? { flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 16px 20px", WebkitOverflowScrolling: "touch", position: "relative", zIndex: 1 }
            : isMobile
            ? { flex: 1, minHeight: 0, overflowY: "auto", padding: "10px 14px", WebkitOverflowScrolling: "touch" }
            : { maxHeight: panelSize?.mh ? `${panelSize.mh}px` : (isTablet ? "min(46vh, 420px)" : "min(48vh, 440px)"), overflowY: "auto", padding: "10px 14px" }}>
            {log.map((l, i) => {
              if (l.from === "viki-status") {
                return (
                  <div key={i} className="flex items-center gap-1.5" style={{ color: C.mutedDim, fontSize: 11, fontStyle: "italic", padding: "2px 4px" }}>
                    <Search size={11} style={{ color: C.cyan, flexShrink: 0 }} />
                    {l.text}
                  </div>
                );
              }
              if (l.from === "viki-stream") {
                return (
                  <div key={i} className="flex" style={{ justifyContent: "flex-start", alignItems: "flex-end", gap: 6 }}>
                    <VikiAvatar size={20} mood="thinking" theme={APP_THEME} style={{ marginBottom: 2 }} />
                    <div data-testid="viki-streaming-bubble" style={{
                      maxWidth: "88%", padding: "9px 12px", borderRadius: "14px 14px 14px 5px", fontSize: isMobile ? 14.5 : 13, lineHeight: 1.6,
                      background: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      <FormattedText text={l.text} />
                      <span className="viki-caret" />
                    </div>
                  </div>
                );
              }
              return (
                <div key={i} className="flex viki-msg-in" style={{ justifyContent: l.from === "viki" ? "flex-start" : "flex-end", alignItems: "flex-end", gap: 6 }}>
                  {l.from === "viki" && <VikiAvatar size={20} theme={APP_THEME} style={{ marginBottom: 2 }} />}
                  <div style={{
                    maxWidth: "88%", padding: "9px 12px", fontSize: isMobile ? 14.5 : 13, lineHeight: 1.6,
                    borderRadius: l.from === "viki" ? "14px 14px 14px 5px" : "14px 14px 5px 14px",
                    background: l.from === "viki" ? C.surfaceAlt
                      : "linear-gradient(135deg, rgba(69,224,208,0.17) 0%, rgba(156,143,240,0.14) 100%)",
                    color: C.text,
                    border: l.from === "viki" ? `1px solid ${C.border}` : `1px solid ${C.cyan}44`,
                    whiteSpace: "pre-wrap", wordBreak: "break-word",
                  }}>
                    {l.isGoal && <div className="flex items-center gap-1 mb-1" style={{ color: C.violet, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}><Target size={10} /> Obiettivo</div>}
                    {l.imagePreview && (
                      <img src={l.imagePreview} alt="allegato" data-testid="viki-msg-image"
                        style={{ maxWidth: "100%", maxHeight: 140, borderRadius: 8, marginBottom: 6, display: "block" }} />
                    )}
                    {l.from === "viki" ? <FormattedText text={l.text} /> : l.text}
                    {l.from === "viki" && l.text && (
                      <div className="flex items-center gap-3" style={{ marginTop: 6 }}>
                        <button onClick={() => handleCopy(l.text, i)} className="mind-btn flex items-center gap-1"
                          style={{ color: C.mutedDim, fontSize: 10, background: "none", border: "none", padding: 0 }}>
                          <Copy size={10} /> {copiedIdx === i ? "Copiato" : "Copia"}
                        </button>
                      </div>
                    )}
                    {l.action && <ProposedActionCard action={l.action} currentPath={location.pathname} />}
                    {l.actions && l.actions.length > 1 && (() => {
                      // Le azioni con conferma rafforzata (ridigitazione/frase scritta) non entrano MAI
                      // nel "Conferma tutto": vengono mostrate come card singole con la loro conferma.
                      const sensitive = l.actions.filter((a) => ACTION_HANDLERS[a.type]?.confirmField?.(a));
                      const normal = l.actions.filter((a) => !ACTION_HANDLERS[a.type]?.confirmField?.(a));
                      return (
                        <>
                          {normal.length > 1 ? <MultiActionCard actions={normal} />
                            : normal.map((a, ai) => <ProposedActionCard key={`n${ai}`} action={a} currentPath={location.pathname} />)}
                          {sensitive.map((a, ai) => <ProposedActionCard key={`s${ai}`} action={a} currentPath={location.pathname} />)}
                        </>
                      );
                    })()}
                    {l.agentActions && (
                      <div className="flex flex-col gap-2 mt-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
                        <span style={{ color: C.mutedDim, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          {l.agentActions.length} {l.agentActions.length === 1 ? "azione proposta" : "azioni proposte"} — da confermare una per una
                        </span>
                        {l.agentActions.map((a, ai) => (
                          <ProposedActionCard key={ai} action={a} currentPath={location.pathname} />
                        ))}
                      </div>
                    )}
                    {l.followups && i === log.length - 1 && !sending && (
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }} data-testid="viki-followup-chips">
                        {l.followups.map((q, qi) => (
                          <button key={qi} data-testid={`viki-followup-chip-${qi}`} onClick={() => sendMessage(q)}
                            style={{ padding: "5px 10px", borderRadius: 20, fontSize: 10.5, fontWeight: 600, background: "rgba(69,224,208,0.10)", color: "#CFFAF5", border: `1px solid ${C.cyan}44`, cursor: "pointer", textAlign: "left" }}>
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                    {l.sources && l.sources.length > 0 && (
                      <div className="flex flex-col gap-1 mt-2 pt-2" style={{ borderTop: `1px solid ${C.border}` }}>
                        <span style={{ color: C.mutedDim, fontSize: 9.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Fonti</span>
                        {l.sources.map((s, si) => (
                          <a key={si} href={s.url} target="_blank" rel="noopener noreferrer"
                            style={{ color: C.cyan, fontSize: 10.5, lineHeight: 1.4, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.title || s.url}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {sending && log[log.length - 1]?.from !== "viki-stream" && (
              <div className="flex items-center gap-1.5 viki-msg-in" style={{ padding: "2px 4px" }}>
                <VikiAvatar size={22} mood="thinking" theme={APP_THEME} />
                {[0, 1, 2].map((d) => (
                  <span key={d} className="viki-dot" style={{ width: 5, height: 5, borderRadius: "50%", background: C.cyan, animationDelay: `${d * 0.15}s` }} />
                ))}
              </div>
            )}
            {log.length === 1 && !sending && (standalone ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "22px 6px 8px", textAlign: "center" }}>
                <div style={{ position: "relative", width: 124, height: 124, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div className="viki-sa-halo" />
                  <div className="viki-sa-float"><VikiAvatar size={98} mood="wave" theme={APP_THEME} /></div>
                </div>
                <div>
                  <div style={{ color: "#fff", fontSize: 19, fontWeight: 800, letterSpacing: -0.3 }}>
                    Ciao{firstName ? ` ${firstName}` : ""}!
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13.5, marginTop: 4 }}>
                    Come posso aiutarti oggi?
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%", maxWidth: 360 }}>
                  {suggestionsFor(location.pathname).map((s, i) => {
                    const Ic = [Eye, Search, Target][i % 3];
                    return (
                      <button key={i} data-testid={`viki-suggestion-chip-${i}`} onClick={() => { setDraft(s); setTimeout(() => inputRef.current?.focus(), 0); }}
                        className="viki-sa-card"
                        style={{ display: "flex", alignItems: "center", gap: 11, textAlign: "left", padding: "13px 15px", borderRadius: 14,
                                 background: "rgba(255,255,255,0.045)", border: "1px solid rgba(255,255,255,0.09)",
                                 color: "rgba(255,255,255,0.9)", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                        <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(69,224,208,0.12)", border: "1px solid rgba(69,224,208,0.3)",
                                       display: "flex", alignItems: "center", justifyContent: "center", color: "#45E0D0", flexShrink: 0 }}>
                          <Ic size={15} />
                        </span>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" style={{ padding: "2px 4px" }}>
                {suggestionsFor(location.pathname).map((s, i) => (
                  <button key={i} data-testid={`viki-suggestion-chip-${i}`} onClick={() => { setDraft(s); setTimeout(() => inputRef.current?.focus(), 0); }}
                    className="mind-btn px-2.5 py-1.5 rounded-full text-[11.5px] font-medium"
                    style={{ background: "rgba(69,224,208,0.12)", color: "#CFFAF5", border: `1px solid ${C.cyan}55` }}>
                    {s}
                  </button>
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className={standalone ? "flex items-center justify-between px-2 pt-2" : "flex items-center gap-1.5 px-2.5 pt-2"}
            style={standalone ? { position: "relative", zIndex: 1, flexShrink: 0 } : { borderTop: `1px solid ${C.border}` }}>
            <div className={standalone ? "flex items-center gap-1.5" : "contents"}>
            <button onClick={() => setAgentMode(false)} disabled={sending}
              className={standalone ? "px-3 py-1.5 rounded-full text-[11.5px] font-bold" : "px-2.5 py-1 rounded-full text-[10px] font-semibold"}
              style={{ background: !agentMode ? C.cyanDim : "transparent", color: C.cyan, opacity: !agentMode ? 1 : 0.55, border: `1px solid ${C.cyan}${!agentMode ? "66" : "33"}` }}>
              Chiedi
            </button>
            <button onClick={() => setAgentMode(true)} disabled={sending}
              className={standalone ? "flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold" : "flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"}
              style={{ display: privileged ? undefined : "none", background: agentMode ? "rgba(56,189,248,0.14)" : "transparent", color: "#38BDF8", opacity: agentMode ? 1 : 0.55, border: `1px solid #38BDF8${agentMode ? "66" : "33"}` }}>
              <Target size={standalone ? 11 : 10} /> Obiettivo
            </button>
            <button onClick={runRecognition} disabled={sending}
              className={standalone ? "flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold" : "flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold"}
              style={{ background: "rgba(242,169,59,0.12)", color: "#F2A93B", border: "1px solid #F2A93B55" }}>
              <Sunrise size={standalone ? 11 : 10} /> Briefing
            </button>
            </div>
            {standalone && (
              <div className="flex items-center gap-1">{renderUtilityBtns(30, 15)}</div>
            )}
          </div>
          {attachedImage && (
            <div className="flex items-center gap-2 px-3 pt-2" data-testid="viki-attached-preview">
              <img src={attachedImage.preview} alt="anteprima" style={{ height: 34, borderRadius: 6, border: `1px solid ${C.border}` }} />
              <span style={{ color: C.mutedDim, fontSize: 10.5 }}>Immagine allegata: VIKI la leggerà col prossimo messaggio</span>
              <button onClick={() => setAttachedImage(null)} title="Rimuovi immagine" data-testid="viki-remove-image-btn"
                className="p-1 rounded-md" style={{ color: C.mutedDim, background: "transparent", border: "none" }}>
                <X size={12} />
              </button>
            </div>
          )}
          <div className={standalone ? "flex items-end gap-1.5 p-2" : "flex items-end gap-2 p-2.5"} style={standalone ? {
            margin: "8px 8px calc(8px + env(safe-area-inset-bottom))",
            background: "rgba(14,22,32,0.92)", border: "1px solid rgba(69,224,208,0.22)", borderRadius: 20,
            backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 0 16px rgba(69,224,208,0.1)",
            position: "relative", zIndex: 1, flexShrink: 0,
          } : undefined}>
            {!standalone && renderUtilityBtns(isMobile ? 40 : 30, 8)}
            <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }}
              data-testid="viki-image-input"
              onChange={(e) => { attachImage(e.target.files?.[0]); e.target.value = ""; }} />
            <textarea
              ref={inputRef}
              rows={1}
              data-testid="viki-chat-input-floating"
              value={draft} onChange={(e) => setDraft(e.target.value)} disabled={sending}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  agentMode ? runAgent() : send();
                }
              }}
              placeholder={handsFree ? (recState === "recording" ? "🎧 Ti ascolto… parla pure, invio da solo quando taci" : recState === "transcribing" ? "Trascrivo quello che hai detto…" : "Modalità mani libere attiva…") : recState === "recording" ? "Sto ascoltando… premi il quadrato per fermare" : recState === "transcribing" ? "Trascrivo quello che hai detto…" : agentMode ? (standalone ? "Descrivi un obiettivo, VIKI lo persegue da sola…" : "Descrivi un obiettivo, VIKI lo persegue da sola… (Maiusc+Invio per andare a capo)") : (standalone ? "Chiedi a V.I.K.I…" : "Scrivi a VIKI… (Maiusc+Invio per andare a capo)")}
              className="flex-1 rounded-xl px-3 py-2 viki-input"
              style={{ background: standalone ? "rgba(255,255,255,0.05)" : C.surfaceAlt, border: `1px solid ${standalone ? "rgba(255,255,255,0.10)" : C.border}`, color: C.text, resize: "none",
                       overflowY: "auto", maxHeight: 160, lineHeight: 1.45, fontFamily: "inherit",
                       fontSize: isMobile ? 16 : 14 }}
            />
            <button onClick={agentMode ? runAgent : send} disabled={sending} className="flex items-center justify-center shrink-0"
              style={standalone
                ? { width: 40, height: 40, borderRadius: 20, background: "linear-gradient(135deg, #45E0D0 0%, #9C8FF0 100%)", color: "#080C11", border: "none", boxShadow: "0 0 14px rgba(69,224,208,0.35)" }
                : { width: isMobile ? 40 : 30, height: isMobile ? 40 : 30, borderRadius: 8, background: C.cyanDim, color: C.cyan, border: `1px solid ${C.cyan}55` }}>
              <ArrowUp size={standalone ? 18 : isMobile ? 17 : 14} strokeWidth={standalone ? 2.6 : 2} />
            </button>
          </div>
          {sending && agentMode && (
            <div className="flex justify-center pb-2">
              <button onClick={() => { const j = agentJobIdRef.current; if (j) { api.post(`/viki/agent/stop/${j}`).catch(() => { stopAgentRef.current = true; }); } else { stopAgentRef.current = true; } }} className="mind-btn flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold"
                style={{ background: "transparent", color: C.mutedDim, border: `1px solid ${C.border}` }}>
                <X size={11} /> Ferma il lavoro
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
